import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Search, Filter, Star, Target, Users, Calendar, CheckCircle, Lightbulb, TrendingUp, Award, X, Plus } from 'lucide-react';
import './ProfileBuilder.css';

const ProfileBuilder = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [profileData, setProfileData] = useState({
    title: '',
    description: '',
    targetDate: '',
    personalGoals: []
  });
  // const [showPreview, setShowPreview] = useState(false); // Reserved for future use
  const [animating, setAnimating] = useState(false);

  const steps = [
    { id: 'template', title: 'Choose Your Path', icon: Target, description: 'Select your career destination' },
    { id: 'components', title: 'Build Experience', icon: Award, description: 'Add meaningful experiences' },
    { id: 'personalize', title: 'Make It Yours', icon: Users, description: 'Customize your story' },
    { id: 'analyze', title: 'Get Insights', icon: TrendingUp, description: 'Review your profile' }
  ];

  const mockTemplates = [
    {
      id: 'ib-analyst',
      title: 'Investment Banking Analyst',
      industry: 'Finance',
      successRate: 78,
      difficulty: 4,
      timeline: '2-3 years',
      description: 'Break into top-tier investment banking with strategic experiences',
      tags: ['Finance', 'Analytics', 'Leadership'],
      themes: ['quantitative', 'leadership', 'networking'],
      color: '#2563eb',
      gradient: 'from-blue-500 to-blue-700'
    },
    {
      id: 'tech-pm',
      title: 'Tech Product Manager',
      industry: 'Technology',
      successRate: 85,
      difficulty: 3,
      timeline: '1-2 years',
      description: 'Lead product development at top tech companies',
      tags: ['Product', 'Strategy', 'Tech'],
      themes: ['innovation', 'leadership', 'analytical'],
      color: '#7c3aed',
      gradient: 'from-purple-500 to-purple-700'
    },
    {
      id: 'social-impact',
      title: 'Social Impact Leader',
      industry: 'Non-Profit',
      successRate: 72,
      difficulty: 3,
      timeline: '1-3 years',
      description: 'Drive meaningful change in communities and organizations',
      tags: ['Impact', 'Leadership', 'Community'],
      themes: ['social_impact', 'leadership', 'communication'],
      color: '#059669',
      gradient: 'from-green-500 to-green-700'
    }
  ];

  const mockComponents = [
    {
      id: 'finance-internship',
      title: 'Finance Internship',
      category: 'Professional Experience',
      difficulty: 3,
      impact: 8,
      description: 'Summer internship at investment firm',
      themes: ['quantitative', 'networking'],
      timeCommitment: '3 months',
      relevantTo: ['ib-analyst', 'tech-pm']
    },
    {
      id: 'startup-founder',
      title: 'Startup Co-Founder',
      category: 'Entrepreneurship',
      difficulty: 5,
      impact: 9,
      description: 'Founded and led early-stage startup',
      themes: ['leadership', 'innovation'],
      timeCommitment: '1-2 years',
      relevantTo: ['tech-pm', 'social-impact']
    },
    {
      id: 'volunteer-leader',
      title: 'Community Volunteer Leader',
      category: 'Social Impact',
      difficulty: 2,
      impact: 7,
      description: 'Led community service initiatives',
      themes: ['social_impact', 'leadership'],
      timeCommitment: '6 months',
      relevantTo: ['social-impact']
    }
  ];

  const handleStepChange = (newStep) => {
    if (newStep >= 0 && newStep < steps.length) {
      setAnimating(true);
      setTimeout(() => {
        setCurrentStep(newStep);
        setAnimating(false);
      }, 150);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return selectedTemplate !== null;
      case 1: return selectedComponents.length > 0;
      case 2: return profileData.title.trim() !== '';
      case 3: return true;
      default: return false;
    }
  };

  // Template Selection Component
  const TemplateSelector = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterIndustry, setFilterIndustry] = useState('');

    const filteredTemplates = mockTemplates.filter(template => {
      const matchesSearch = template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           template.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = !filterIndustry || template.industry === filterIndustry;
      return matchesSearch && matchesFilter;
    });

    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-gray-900">Choose Your Career Path</h2>
          <p className="text-gray-600">Select a template that matches your aspirations</p>
        </div>

        {/* Search and Filter */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={filterIndustry}
              onChange={(e) => setFilterIndustry(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Industries</option>
              <option value="Finance">Finance</option>
              <option value="Technology">Technology</option>
              <option value="Non-Profit">Non-Profit</option>
            </select>
          </div>
        </div>

        {/* Template Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map(template => (
            <div
              key={template.id}
              onClick={() => setSelectedTemplate(template)}
              className={`group cursor-pointer transform transition-all duration-200 hover:scale-105 ${
                selectedTemplate?.id === template.id ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden">
                <div className={`h-32 bg-gradient-to-r ${template.gradient} relative`}>
                  <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="text-lg font-semibold">{template.title}</h3>
                    <p className="text-sm opacity-90">{template.industry}</p>
                  </div>
                  {selectedTemplate?.id === template.id && (
                    <div className="absolute top-4 right-4">
                      <CheckCircle className="h-6 w-6 text-white" />
                    </div>
                  )}
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-sm text-gray-600">{template.description}</p>
                  
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-yellow-400" />
                      <span className="font-medium">{template.successRate}% success</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span>{template.timeline}</span>
                    </div>
                  </div>

                  <div className="flex gap-1 flex-wrap">
                    {template.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Component Selector
  const ComponentSelector = () => {
    const [searchTerm, setSearchTerm] = useState('');
    
    const relevantComponents = mockComponents.filter(comp => 
      !selectedTemplate || comp.relevantTo.includes(selectedTemplate.id)
    );

    const filteredComponents = relevantComponents.filter(comp =>
      comp.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comp.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const toggleComponent = (component) => {
      setSelectedComponents(prev => {
        const exists = prev.find(c => c.id === component.id);
        if (exists) {
          return prev.filter(c => c.id !== component.id);
        } else {
          return [...prev, component];
        }
      });
    };

    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-gray-900">Build Your Experience</h2>
          <p className="text-gray-600">Select experiences that tell your story</p>
          {selectedTemplate && (
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
              <Target className="h-4 w-4" />
              Building path to: {selectedTemplate.title}
            </div>
          )}
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Search experiences..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {filteredComponents.map(component => {
            const isSelected = selectedComponents.find(c => c.id === component.id);
            return (
              <div
                key={component.id}
                onClick={() => toggleComponent(component)}
                className={`cursor-pointer p-4 border-2 rounded-xl transition-all duration-200 ${
                  isSelected 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{component.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{component.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>Impact: {component.impact}/10</span>
                      <span>Difficulty: {component.difficulty}/5</span>
                      <span>{component.timeCommitment}</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    {isSelected ? (
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 border-2 border-gray-300 rounded-full"></div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Personalization Step
  const PersonalizationStep = () => {
    const addGoal = () => {
      setProfileData(prev => ({
        ...prev,
        personalGoals: [...prev.personalGoals, '']
      }));
    };

    const updateGoal = (index, value) => {
      setProfileData(prev => ({
        ...prev,
        personalGoals: prev.personalGoals.map((goal, i) => i === index ? value : goal)
      }));
    };

    const removeGoal = (index) => {
      setProfileData(prev => ({
        ...prev,
        personalGoals: prev.personalGoals.filter((_, i) => i !== index)
      }));
    };

    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-gray-900">Personalize Your Profile</h2>
          <p className="text-gray-600">Make this journey uniquely yours</p>
        </div>

        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profile Title *
            </label>
            <input
              type="text"
              placeholder="My Journey to Investment Banking"
              value={profileData.title}
              onChange={(e) => setProfileData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Story
            </label>
            <textarea
              placeholder="Describe your career aspirations and what drives you..."
              value={profileData.description}
              onChange={(e) => setProfileData(prev => ({ ...prev, description: e.target.value }))}
              rows={4}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Achievement Date
            </label>
            <input
              type="date"
              value={profileData.targetDate}
              onChange={(e) => setProfileData(prev => ({ ...prev, targetDate: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Personal Goals
              </label>
              <button
                onClick={addGoal}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
              >
                <Plus className="h-4 w-4" />
                Add Goal
              </button>
            </div>
            <div className="space-y-2">
              {profileData.personalGoals.map((goal, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    placeholder={`Goal ${index + 1}`}
                    value={goal}
                    onChange={(e) => updateGoal(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={() => removeGoal(index)}
                    className="p-2 text-gray-400 hover:text-red-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Analysis Step
  const AnalysisStep = () => {
    const successProbability = selectedTemplate ? selectedTemplate.successRate + (selectedComponents.length * 3) : 0;
    const completionScore = Math.min(successProbability, 95);

    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-gray-900">Your Profile Analysis</h2>
          <p className="text-gray-600">Here's how your profile looks</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Success Probability */}
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Success Probability</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-green-600">{completionScore}%</span>
                <span className="text-sm text-gray-500">Likely to succeed</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${completionScore}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Profile Completion */}
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Profile Strength</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-blue-600">{selectedComponents.length}/5</span>
                <span className="text-sm text-gray-500">Components</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${Math.min(selectedComponents.length * 20, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Summary */}
        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Profile Summary</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Target:</span>
              <p className="text-gray-600">{selectedTemplate?.title || 'None selected'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Industry:</span>
              <p className="text-gray-600">{selectedTemplate?.industry || 'Not specified'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Timeline:</span>
              <p className="text-gray-600">{selectedTemplate?.timeline || 'Not set'}</p>
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-amber-50 border border-amber-200 p-6 rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <Lightbulb className="h-5 w-5 text-amber-600" />
            <h3 className="font-semibold text-amber-900">Recommendations</h3>
          </div>
          <div className="space-y-2 text-sm text-amber-800">
            {selectedComponents.length < 3 && (
              <p>• Consider adding more experiences to strengthen your profile</p>
            )}
            {!profileData.targetDate && (
              <p>• Set a target completion date to track your progress</p>
            )}
            <p>• Network with professionals in your target industry</p>
            <p>• Regularly update your profile as you gain new experiences</p>
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    const components = [TemplateSelector, ComponentSelector, PersonalizationStep, AnalysisStep];
    const CurrentComponent = components[currentStep];
    return <CurrentComponent />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Academic Profile Builder
            </h1>
            <p className="text-gray-600">
              Build a data-driven profile to achieve your career goals
            </p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              const isAccessible = index <= currentStep || canProceed();
              
              return (
                <React.Fragment key={step.id}>
                  <div 
                    onClick={() => isAccessible && handleStepChange(index)}
                    className={`flex flex-col items-center cursor-pointer transition-all duration-200 ${
                      isAccessible ? 'hover:scale-105' : 'cursor-not-allowed'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isCompleted 
                        ? 'bg-green-500 text-white' 
                        : isActive 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 text-gray-500'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle className="h-6 w-6" />
                      ) : (
                        <Icon className="h-6 w-6" />
                      )}
                    </div>
                    <div className="text-center mt-2">
                      <p className={`text-sm font-medium ${
                        isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                      }`}>
                        {step.title}
                      </p>
                      <p className="text-xs text-gray-400">{step.description}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-4 transition-all duration-300 ${
                      index < currentStep ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className={`transition-all duration-300 ${animating ? 'opacity-0 transform translate-y-4' : 'opacity-100 transform translate-y-0'}`}>
          {renderCurrentStep()}
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border-t border-gray-200 sticky bottom-0">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => handleStepChange(currentStep - 1)}
              disabled={currentStep === 0}
              className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-lg text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors duration-200"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">
                Step {currentStep + 1} of {steps.length}
              </span>
            </div>

            {currentStep === steps.length - 1 ? (
              <button
                onClick={() => alert('Profile saved!')}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
              >
                Save Profile
                <CheckCircle className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={() => handleStepChange(currentStep + 1)}
                disabled={!canProceed()}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors duration-200"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileBuilder;
